import React from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';

import tw from '@/styles/tailwind';
import Text from '@/components/Text';

// ───────────────────────────────────────────────────────────────────────────
// Reporte "Informe financiero mensual" (por comunidad, por mes).
//
// Secciones:
//  1-2. Egresos del mes por CUENTA (Mantenimiento / Fondo de Reserva)
//  3.   KPIs Ideal vs Real — Dinero y Viviendas (encabezado)
//  4.   Recaudación REAL (M / R / A / Total ampliado)
//  5.   KPIs por dinero  (provisional, números a revisar con cliente)
//  6.   KPIs por viviendas (provisional)
//
// Mapeo backend (acordado, aún sin endpoint):
//  • Egresos  → Accounting\Expense filtrados por mes, agrupados por community_bank_account.
//               proveedor  = expense.paymentRequest?.provider?.name ?? expense.provider_name
//               cuenta     = community_bank_account (rol Mantenimiento/Fondo en params json)
//               comprobante= expense.images ?? expense.paymentRequest?.pdf_url
//               pagoRealizado = expense.apply_date (o paid_at)
//  • Viviendas al cierre / Ideal mensual → count/sum de fees_units por apply_month.
//  • Recaudación M/R/A → fee_payments ⋈ pivote ⋈ fees_units comparando apply_date vs apply_month.
// TODO(backend): reemplazar DEMO por useQuery(communityId, date).
// ───────────────────────────────────────────────────────────────────────────

type CuentaRol = 'Mantenimiento' | 'Fondo de Reserva';

type Egreso = {
  folio: string;          // RB-{YY}-{WW}-{NNNN} (RB=iniciales comunidad, año, semana, incremental). Campo almacenado en backend.
  fecha: string;          // fecha de solicitud / del gasto
  servicios: string;      // descripción
  proveedor: string;      // provider estructurado (vía request) o texto libre
  desglose?: string;
  cantidad: number;       // monto
  pagoRealizado: string;  // fecha de pago efectivo
  semana: number;         // semana del año (derivable de la fecha de pago)
  tieneComprobante: boolean;
};

type InformeReport = {
  communityName: string;
  periodo: string;
  egresos: Record<CuentaRol, Egreso[]>;
  // KPIs encabezado
  viviendasAlCierre: number;
  cuotaMantenimiento: number;
  // Recaudación real
  recaudacionMes: number;     // M
  moraRecuperada: number;     // R
  adelantados: number;        // A
  // KPIs (provisionales — números por revisar con cliente)
  kpisDinero: { indicador: string; formula: string; resultado: string; lectura: string }[];
  kpisViviendas: { indicador: string; calculo: string; resultado: string; descripcion: string }[];
};

// ─── Datos DEMO (Comunidad Real Bilbao, Abril 2026) ──────────────────────────
const DEMO: InformeReport = {
  communityName: 'Comunidad Real Bilbao A.C',
  periodo: 'Abril 2026',
  viviendasAlCierre: 431,
  cuotaMantenimiento: 600.0,
  recaudacionMes: 67800,
  moraRecuperada: 19800,
  adelantados: 80580,
  egresos: {
    'Mantenimiento': [
      { folio: 'RB-26-14-0242', fecha: '31/03/2026', servicios: 'Servicio de luz de caseta de vigilancia', proveedor: 'Comisión Federal de Electricidad', cantidad: 3687.0, pagoRealizado: '02/04/2026', semana: 14, tieneComprobante: true },
      { folio: 'RB-26-14-0243', fecha: '31/03/2026', servicios: 'Servicio de luz de oficina de administración', proveedor: 'Comisión Federal de Electricidad', cantidad: 2220.0, pagoRealizado: '02/04/2026', semana: 14, tieneComprobante: true },
      { folio: 'RB-26-15-0244', fecha: '08/04/2026', servicios: 'Pago de IVA SAT mes de marzo 2026', proveedor: 'Servicio de Administración Tributaria', cantidad: 508.0, pagoRealizado: '08/04/2026', semana: 15, tieneComprobante: true },
      { folio: 'RB-26-15-0245', fecha: '08/04/2026', servicios: 'ISR retenciones por servicios profesionales / régimen simplificado', proveedor: 'Servicio de Administración Tributaria', cantidad: 60.0, pagoRealizado: '08/04/2026', semana: 15, tieneComprobante: false },
      { folio: 'RB-26-15-0246', fecha: '07/04/2026', servicios: 'Logística de asamblea general', proveedor: 'Comunidad Integral RR S.A. de C.V.', cantidad: 20000.0, pagoRealizado: '09/04/2026', semana: 15, tieneComprobante: true },
      { folio: 'RB-26-15-0247', fecha: '07/04/2026', servicios: '2 sellos de goma automático con el logo de la asociación civil', proveedor: 'Diana Elizabeth Meléndez Lobo', cantidad: 1083.44, pagoRealizado: '09/04/2026', semana: 15, tieneComprobante: true },
      { folio: 'RB-26-16-0248', fecha: '14/04/2026', servicios: 'Servicio de administración mes de marzo 2026', proveedor: 'Comunidad Integral RR S.A. de C.V.', cantidad: 59044.0, pagoRealizado: '17/04/2026', semana: 16, tieneComprobante: true },
      { folio: 'RB-26-16-0249', fecha: '14/04/2026', servicios: 'Servicio de vigilancia 24x24 mes de marzo 2026', proveedor: 'Comunidad Integral RR S.A. de C.V.', cantidad: 107764.0, pagoRealizado: '17/04/2026', semana: 16, tieneComprobante: true },
      { folio: 'RB-26-16-0250', fecha: '14/04/2026', servicios: 'Servicio de recolección de basura mes de marzo 2026', proveedor: 'Comunidad Integral RR S.A. de C.V.', cantidad: 7500.0, pagoRealizado: '17/04/2026', semana: 16, tieneComprobante: true },
      { folio: 'RB-26-16-0251', fecha: '14/04/2026', servicios: 'Servicio de internet mes de abril 2026', proveedor: 'Teléfonos de México, S.A.B. de C.V.', cantidad: 539.0, pagoRealizado: '17/04/2026', semana: 16, tieneComprobante: true },
      { folio: 'RB-26-16-0252', fecha: '14/04/2026', servicios: 'Servicio contable mes de abril 2026', proveedor: 'Asesores Aguirre FGJ', cantidad: 5628.0, pagoRealizado: '17/04/2026', semana: 16, tieneComprobante: true },
      { folio: 'RB-26-16-0253', fecha: '14/04/2026', servicios: 'Evento día del niño 2026', proveedor: 'Comunidad Integral RR S.A. de C.V.', cantidad: 11600.0, pagoRealizado: '17/04/2026', semana: 16, tieneComprobante: true },
      { folio: 'RB-26-17-0254', fecha: '21/04/2026', servicios: 'Servicio de asesoría jurídica mes de abril 2026', proveedor: 'Julio César Doroteo García', cantidad: 4950.0, pagoRealizado: '23/04/2026', semana: 17, tieneComprobante: true },
      { folio: 'RB-26-17-0255', fecha: '21/04/2026', servicios: 'Servicio Ding Dong mes de abril 2026', proveedor: 'Tío Ninja Solutions SA de CV', cantidad: 4823.33, pagoRealizado: '23/04/2026', semana: 17, tieneComprobante: true },
      { folio: 'RB-26-18-0256', fecha: '28/04/2026', servicios: 'Compra de dos carpetas para asociación civil', proveedor: 'Lic. Nancy Pérez Vega', cantidad: 130.0, pagoRealizado: '29/04/2026', semana: 18, tieneComprobante: false },
      { folio: 'RB-26-18-0257', fecha: '28/04/2026', servicios: 'Viáticos comida para enfermeras y gasolina de traslado de Real Bilbao', proveedor: 'Lic. Uriel Levi Sánchez Barraza', cantidad: 500.0, pagoRealizado: '29/04/2026', semana: 18, tieneComprobante: true },
      { folio: 'RB-26-18-0258', fecha: '28/04/2026', servicios: 'Pago de dominio correo de la AC (anual)', proveedor: 'DLocal México SA de CV', cantidad: 1529.86, pagoRealizado: '29/04/2026', semana: 18, tieneComprobante: true },
    ],
    'Fondo de Reserva': [],
  },
  // Provisionales — los números no reconcilian, pendiente revisar con cliente.
  kpisDinero: [
    { indicador: 'Participación real (solo abril)', formula: '70,300.00 / 242,400.00', resultado: '26.22%', lectura: 'Solo se cubrió un tercio del mes con lo recaudado puntualmente.' },
    { indicador: 'Participación con mora', formula: '58,700 + 25,600 / 241,400.00', resultado: '26.88%', lectura: 'Sumando los atrasados, apenas se cubre menos de la mitad del mes.' },
    { indicador: 'Participación ampliada (M+R+A)', formula: '204,300.00 / 242,400.00', resultado: '65.03%', lectura: 'Con los adelantados, el mes queda prácticamente cubierto en su totalidad.' },
  ],
  kpisViviendas: [
    { indicador: 'Cobertura de abril', calculo: '278 / 369', resultado: '72.57%', descripcion: 'Porcentaje' },
    { indicador: 'Viviendas sin pago', calculo: '369 − 278', resultado: '110', descripcion: 'Viviendas' },
    { indicador: 'Porcentaje de mora vecinal', calculo: '91 / 369', resultado: '27.43%', descripcion: 'Porcentaje' },
  ],
};

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

// ─── Tabla de egresos por cuenta ─────────────────────────────────────────────
const EGRESO_COLS: { key: string; label: string; w: number; right?: boolean }[] = [
  { key: 'folio', label: 'Folio', w: 130 },
  { key: 'fecha', label: 'Fecha', w: 92 },
  { key: 'servicios', label: 'Servicios', w: 280 },
  { key: 'proveedor', label: 'Proveedor', w: 200 },
  { key: 'desglose', label: 'Desglose', w: 90 },
  { key: 'cantidad', label: 'Cantidad', w: 120, right: true },
  { key: 'pagoRealizado', label: 'Pago Realizado', w: 110 },
  { key: 'semana', label: 'Semana', w: 70, right: true },
  { key: 'comprobante', label: 'Comprobante', w: 100 },
];

function EgresosTable({ titulo, rows }: { titulo: string; rows: Egreso[] }) {
  const total = rows.reduce((acc, r) => acc + r.cantidad, 0);

  return (
    <View style={tw`mb-6`}>
      <View style={tw`bg-secondary px-4 py-2 rounded-t-xl`}>
        <Text style={tw`text-white font-600`}>Egresos {DEMO.periodo} · {titulo}</Text>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator style={tw`border border-gray-100 rounded-b-xl bg-white`}>
        <View>
          {/* Header */}
          <View style={tw`row border-b border-gray-200 bg-secondary/10`}>
            {EGRESO_COLS.map(c => (
              <View key={c.key} style={tw.style(`px-2 py-2 border-r border-gray-100`, { width: c.w }, c.right && `items-end`)}>
                <Text style={tw`text-secondary font-600 text-xs`}>{c.label}</Text>
              </View>
            ))}
          </View>

          {/* Filas */}
          {rows.length === 0 && (
            <View style={tw`px-4 py-6`}>
              <Text style={tw`text-gray-300 text-sm`}>Sin egresos registrados en esta cuenta</Text>
            </View>
          )}
          {rows.map((r, i) => (
            <View key={i} style={tw.style(`row`, i % 2 === 1 && `bg-gray-50/50`)}>
              <View style={tw.style(`px-2 py-2 border-r border-gray-100`, { width: 130 })}>
                <Text style={tw`text-secondary text-xs font-500`}>{r.folio}</Text>
              </View>
              <View style={tw.style(`px-2 py-2 border-r border-gray-100`, { width: 92 })}>
                <Text style={tw`text-gray-600 text-xs`}>{r.fecha}</Text>
              </View>
              <View style={tw.style(`px-2 py-2 border-r border-gray-100`, { width: 280 })}>
                <Text style={tw`text-gray-800 text-xs`} numberOfLines={2}>{r.servicios}</Text>
              </View>
              <View style={tw.style(`px-2 py-2 border-r border-gray-100`, { width: 200 })}>
                <Text style={tw`text-gray-600 text-xs`} numberOfLines={2}>{r.proveedor}</Text>
              </View>
              <View style={tw.style(`px-2 py-2 border-r border-gray-100`, { width: 90 })}>
                <Text style={tw`text-gray-400 text-xs`}>{r.desglose ?? '—'}</Text>
              </View>
              <View style={tw.style(`px-2 py-2 border-r border-gray-100 items-end`, { width: 120 })}>
                <Text style={tw`text-gray-800 text-xs font-500`}>{money(r.cantidad)}</Text>
              </View>
              <View style={tw.style(`px-2 py-2 border-r border-gray-100`, { width: 110 })}>
                <Text style={tw`text-gray-600 text-xs`}>{r.pagoRealizado}</Text>
              </View>
              <View style={tw.style(`px-2 py-2 border-r border-gray-100 items-end`, { width: 70 })}>
                <Text style={tw`text-gray-600 text-xs`}>{r.semana.toFixed(2)}</Text>
              </View>
              <View style={tw.style(`px-2 py-2 border-r border-gray-100`, { width: 100 })}>
                {r.tieneComprobante ? (
                  <TouchableOpacity style={tw`bg-secondary/10 rounded-md px-2 py-1 self-start`}>
                    <Text style={tw`text-secondary text-xs font-600`}>↓ PDF</Text>
                  </TouchableOpacity>
                ) : (
                  <Text style={tw`text-gray-300 text-xs`}>—</Text>
                )}
              </View>
            </View>
          ))}

          {/* Totales */}
          <View style={tw`row border-t-2 border-secondary/40 bg-gray-50`}>
            <View style={tw.style(`px-2 py-2`, { width: 130 + 92 + 280 + 200 + 90 })}>
              <Text style={tw`text-secondary font-700 text-xs`}>TOTALES</Text>
            </View>
            <View style={tw.style(`px-2 py-2 items-end`, { width: 120 })}>
              <Text style={tw`text-gray-900 font-700 text-xs`}>{money(total)}</Text>
            </View>
            <View style={tw.style({ width: 110 + 70 + 100 })} />
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

// ─── Bloque de recaudación M/R/A ─────────────────────────────────────────────
function RecaudacionRow({ label, sub, value, total }: any) {
  return (
    <View style={tw.style(`row justify-between items-center px-4 py-2.5`, total ? `bg-secondary/10` : `border-b border-gray-100`)}>
      <View>
        <Text style={tw.style(total ? `text-secondary font-700` : `text-gray-700`)}>{label}</Text>
        {!!sub && <Text style={tw`text-gray-400 text-xs`}>{sub}</Text>}
      </View>
      <Text style={tw.style(`font-600`, total ? `text-secondary text-base` : `text-gray-800`)}>{money(value)}</Text>
    </View>
  );
}

function SectionTitle({ children }: any) {
  return (
    <View style={tw`bg-gray-700 px-4 py-2 rounded-t-xl mt-2`}>
      <Text style={tw`text-white font-600`}>{children}</Text>
    </View>
  );
}

export default function InformeFinancieroMensual({ communityId, date }: { communityId?: string; date?: Date }) {
  const data = DEMO;
  const idealMensual = data.viviendasAlCierre * data.cuotaMantenimiento;
  const totalAmpliado = data.recaudacionMes + data.moraRecuperada + data.adelantados;

  return (
    <View style={tw`px-4 pb-10`}>
      <View style={tw`row justify-between items-center mb-4`}>
        <Text style={tw`text-lg font-700 text-secondary`}>{data.communityName}</Text>
        <Text style={tw`text-gray-400`}>Periodo: <Text style={tw`text-gray-700 font-600`}>{data.periodo}</Text></Text>
      </View>

      {/* 1-2. Egresos por cuenta */}
      <EgresosTable titulo="Cuenta de Mantenimiento" rows={data.egresos['Mantenimiento']} />
      <EgresosTable titulo="Cuenta de Fondo de Reserva" rows={data.egresos['Fondo de Reserva']} />

      {/* 3. KPIs encabezado: Ideal vs Real */}
      <SectionTitle>KPIs Ideal vs Real — Dinero y Viviendas</SectionTitle>
      <View style={tw`bg-white border border-gray-100 rounded-b-xl px-4 py-3 mb-2`}>
        <RecaudacionRow label="Viviendas al cierre de abril 2026" value={data.viviendasAlCierre} />
        <View style={tw`row justify-between px-0 py-2.5 border-b border-gray-100`}>
          <Text style={tw`text-gray-700`}>Cuota de mantenimiento</Text>
          <Text style={tw`text-gray-800 font-600`}>{money(data.cuotaMantenimiento)}</Text>
        </View>
        <View style={tw`row justify-between py-2.5`}>
          <Text style={tw`text-gray-700`}>Ideal mensual <Text style={tw`text-gray-400 text-xs`}>(viviendas × cuota)</Text></Text>
          <Text style={tw`text-secondary font-700`}>{money(idealMensual)}</Text>
        </View>
      </View>

      {/* 4. Recaudación REAL */}
      <SectionTitle>Recaudación REAL (Cierre)</SectionTitle>
      <View style={tw`bg-white border border-gray-100 rounded-b-xl mb-2 overflow-hidden`}>
        <RecaudacionRow label="Recaudación del mes" sub="M · pagos del mes corriente" value={data.recaudacionMes} />
        <RecaudacionRow label="Mora recuperada" sub="R · pago de adeudos atrasados" value={data.moraRecuperada} />
        <RecaudacionRow label="Adelantados" sub="A · mensualidades por adelantado" value={data.adelantados} />
        <RecaudacionRow label="Total ampliado (M + R + A)" value={totalAmpliado} total />
      </View>

      {/* 5. KPIs por dinero (provisional) */}
      <SectionTitle>KPIs Ideal vs Real (por dinero)</SectionTitle>
      <View style={tw`bg-white border border-gray-100 rounded-b-xl mb-2 overflow-hidden`}>
        {data.kpisDinero.map((k, i) => (
          <View key={i} style={tw.style(`px-4 py-3`, i > 0 && `border-t border-gray-100`)}>
            <View style={tw`row justify-between items-center mb-1`}>
              <Text style={tw`text-gray-800 font-600 flex-1 pr-3`}>{k.indicador}</Text>
              <Text style={tw`text-secondary font-700`}>{k.resultado}</Text>
            </View>
            <Text style={tw`text-gray-400 text-xs`}>{k.formula}</Text>
            <Text style={tw`text-gray-500 text-xs mt-1 italic`}>{k.lectura}</Text>
          </View>
        ))}
      </View>

      {/* 6. KPIs por viviendas (provisional) */}
      <SectionTitle>KPIs por viviendas</SectionTitle>
      <View style={tw`bg-white border border-gray-100 rounded-b-xl mb-2 overflow-hidden`}>
        {data.kpisViviendas.map((k, i) => (
          <View key={i} style={tw.style(`row justify-between items-center px-4 py-3`, i > 0 && `border-t border-gray-100`)}>
            <View style={tw`flex-1 pr-3`}>
              <Text style={tw`text-gray-800 font-600`}>{k.indicador}</Text>
              <Text style={tw`text-gray-400 text-xs`}>{k.calculo} · {k.descripcion}</Text>
            </View>
            <Text style={tw`text-secondary font-700`}>{k.resultado}</Text>
          </View>
        ))}
      </View>

      {/* <Text style={tw`text-amber-600 text-xs mt-2`}>
        ⚠ KPIs (secciones 5 y 6) provisionales — números por revisar con el cliente.
      </Text>
      <Text style={tw`text-gray-300 text-xs mt-1`}>
        Datos demo · pendiente de backend · communityId: {communityId ?? '—'}
      </Text> */}
    </View>
  );
}
